const FLY_HOLD_MS = 300;
const FLY_START_SIZE = 120;
const FLY_END_SIZE = 28;
const FLY_PADDING = 10;
const FLY_END_PADDING = 2;
const FLY_DURATION_MS = 650;

/** @type {WeakMap<Element, { succeed: (onCartBump?: () => void) => void, fail: () => void }>} */
const optimisticSessions = new WeakMap();

/** @type {WeakMap<Element, Element>} */
const flyOriginOverrides = new WeakMap();

/**
 * @param {Element | null | undefined} el
 * @returns {boolean}
 */
const isElementVisible = (el) => {
  if (!el) return false;

  const style = window.getComputedStyle(el);

  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  return el.getClientRects().length > 0;
};

/**
 * @returns {Element | null}
 */
const getCartIconTarget = () => {
  const headerCart = document.getElementById('cart-icon-bubble');
  const dockCart = document.getElementById('DockCartTrigger');
  const preferDock = window.matchMedia('(width <= 74.99em)').matches;

  if (preferDock && dockCart && isElementVisible(dockCart)) {
    return dockCart.querySelector('.icon') || dockCart;
  }

  if (headerCart && isElementVisible(headerCart)) {
    return headerCart.querySelector('.header__icon-visual') || headerCart;
  }

  if (dockCart && isElementVisible(dockCart)) {
    return dockCart.querySelector('.icon') || dockCart;
  }

  return dockCart?.querySelector('.icon') || dockCart || headerCart?.querySelector('.header__icon-visual') || headerCart;
};

/**
 * @param {Element} cartTarget
 * @returns {{ endX: number, endY: number }}
 */
const getEndPositionForCartTarget = (cartTarget) => {
  const cartRect = cartTarget.getBoundingClientRect();
  const endX = cartRect.left + cartRect.width / 2 - FLY_END_SIZE / 2;
  const endY = cartRect.top + cartRect.height / 2 - FLY_END_SIZE / 2;

  return { endX, endY };
};

/**
 * @param {Element | null | undefined} productRoot
 * @returns {HTMLImageElement | null}
 */
const getVisibleGalleryImage = (productRoot) => {
  if (!productRoot) return null;

  const gallery = productRoot.querySelector('.product__media-gallery');

  if (!gallery) {
    const fallback = productRoot.querySelector('.product-media__image');
    return fallback instanceof HTMLImageElement ? fallback : null;
  }

  const slides = gallery.children;

  if (!slides.length) {
    const fallback = productRoot.querySelector('.product-media__image');
    return fallback instanceof HTMLImageElement ? fallback : null;
  }

  const scrollLeft = gallery.scrollLeft;
  const viewportWidth = gallery.clientWidth;
  let bestSlide = slides[0];
  let bestOverlap = 0;

  for (const slide of slides) {
    const slideLeft = slide.offsetLeft;
    const slideRight = slideLeft + slide.offsetWidth;
    const visibleLeft = Math.max(slideLeft, scrollLeft);
    const visibleRight = Math.min(slideRight, scrollLeft + viewportWidth);
    const overlap = Math.max(0, visibleRight - visibleLeft);

    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      bestSlide = slide;
    }
  }

  const image = bestSlide.querySelector('.product-media__image') || bestSlide.querySelector('img');

  return image instanceof HTMLImageElement ? image : null;
};

/**
 * @param {HTMLImageElement} sourceImage
 * @returns {string}
 */
const getImageSrcForFly = (sourceImage) => {
  return sourceImage.currentSrc || sourceImage.src || '';
};

/**
 * @param {Element} productFormEl
 * @param {Element} originEl
 */
const setFlyToCartOrigin = (productFormEl, originEl) => {
  if (productFormEl instanceof Element && originEl instanceof Element) {
    flyOriginOverrides.set(productFormEl, originEl);
  }
};

const getAddToCartOrigin = (productFormEl) => {
  if (!(productFormEl instanceof Element)) return null;

  const override = flyOriginOverrides.get(productFormEl);
  flyOriginOverrides.delete(productFormEl);

  if (override instanceof Element) {
    return override;
  }

  const submit =
    productFormEl.querySelector('.product-form__submit') ||
    productFormEl.querySelector('button[type="submit"][name="add"]') ||
    productFormEl.querySelector('button[type="submit"]');

  return submit instanceof Element ? submit : null;
};

/**
 * @param {string} imageSrc
 * @returns {Promise<void>}
 */
const preloadFlyImage = (imageSrc) =>
  new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';

    const finish = () => {
      resolve();
    };

    img.addEventListener('load', finish, { once: true });
    img.addEventListener('error', finish, { once: true });
    img.src = imageSrc;

    if (img.complete) {
      finish();
    }
  });

/**
 * @param {Element | null | undefined} cartTarget
 * @param {(() => void) | undefined} onBump
 */
const bumpCartTarget = (cartTarget, onBump) => {
  if (onBump) {
    onBump();
  }

  const freshTarget = getCartIconTarget() || cartTarget;
  const bumpEl = freshTarget?.closest('#DockCartTrigger, #cart-icon-bubble') || freshTarget;

  if (!bumpEl) return;

  bumpEl.classList.remove('fly-to-cart-target--bump');

  // Force reflow so repeated adds retrigger the animation.
  void bumpEl.offsetWidth;

  bumpEl.classList.add('fly-to-cart-target--bump');

  window.setTimeout(() => {
    bumpEl.classList.remove('fly-to-cart-target--bump');
  }, 400);
};

/**
 * @param {DOMRect} originRect
 * @returns {{ startX: number, startY: number }}
 */
const getStartPositionFromOrigin = (originRect) => {
  const startX = originRect.left + originRect.width / 2 - FLY_START_SIZE / 2;
  const startY = originRect.top + originRect.height / 2 - FLY_START_SIZE / 2;

  return { startX, startY };
};

/**
 * @param {string} imageSrc
 * @param {number} startX
 * @param {number} startY
 * @returns {HTMLDivElement}
 */
const createFlyElement = (imageSrc, startX, startY) => {
  const flyEl = document.createElement('div');
  flyEl.className = 'fly-to-cart fly-to-cart--hold';
  flyEl.setAttribute('aria-hidden', 'true');

  const img = document.createElement('img');
  img.className = 'fly-to-cart__image';
  img.src = imageSrc;
  img.alt = '';
  img.decoding = 'async';

  flyEl.appendChild(img);
  flyEl.style.width = `${FLY_START_SIZE}px`;
  flyEl.style.height = `${FLY_START_SIZE}px`;
  flyEl.style.left = `${startX}px`;
  flyEl.style.top = `${startY}px`;

  document.body.appendChild(flyEl);

  return flyEl;
};

/**
 * @param {HTMLDivElement} flyEl
 * @param {number} startX
 * @param {number} startY
 * @param {number} endX
 * @param {number} endY
 * @param {Element} cartTarget
 * @param {() => void} onAnimationEnd
 */
const runFlyAnimation = (flyEl, startX, startY, endX, endY, cartTarget, onAnimationEnd) => {
  flyEl.classList.remove('fly-to-cart--hold');

  const animation = flyEl.animate(
    [
      {
        left: `${startX}px`,
        top: `${startY}px`,
        width: `${FLY_START_SIZE}px`,
        height: `${FLY_START_SIZE}px`,
        padding: '0px',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        opacity: 1,
      },
      {
        left: `${startX}px`,
        top: `${startY}px`,
        width: `${FLY_START_SIZE}px`,
        height: `${FLY_START_SIZE}px`,
        padding: `${FLY_PADDING}px`,
        backgroundColor: 'var(--warm-white)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
        opacity: 1,
        offset: 0.06,
      },
      {
        left: `${endX}px`,
        top: `${endY}px`,
        width: `${FLY_END_SIZE}px`,
        height: `${FLY_END_SIZE}px`,
        padding: `${FLY_END_PADDING}px`,
        backgroundColor: 'var(--warm-white)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
        opacity: 1,
        offset: 0.82,
      },
      {
        left: `${endX}px`,
        top: `${endY}px`,
        width: `${FLY_END_SIZE}px`,
        height: `${FLY_END_SIZE}px`,
        padding: `${FLY_END_PADDING}px`,
        backgroundColor: 'var(--warm-white)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
        opacity: 0,
      },
    ],
    {
      duration: FLY_DURATION_MS,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      fill: 'forwards',
    },
  );

  animation.onfinish = () => {
    flyEl.remove();
    onAnimationEnd(cartTarget);
  };

  return animation;
};

/**
 * @param {Element | null | undefined} productFormEl
 * @param {{ onCartBump?: () => void }} [options]
 * @returns {Promise<boolean>}
 */
const flyProductImageToCart = async (productFormEl, options = {}) => {
  const session = await startOptimisticFlyToCart(productFormEl);

  if (!session) return false;

  if (options.onCartBump) {
    session.succeed(options.onCartBump);
  }

  return true;
};

/**
 * @param {Element | null | undefined} upsellHostEl
 * @returns {Element | null}
 */
const getUpsellCardFromHost = (upsellHostEl) => {
  if (!(upsellHostEl instanceof Element)) return null;

  return (
    upsellHostEl.closest('.product-upsell__card') || upsellHostEl.querySelector('.product-upsell__card')
  );
};

/**
 * @param {Element | null | undefined} upsellFormEl
 * @returns {{ title: string, imageSrc: string }}
 */
const getUpsellFlyMedia = (upsellFormEl) => {
  const card = getUpsellCardFromHost(upsellFormEl);
  const image = card?.querySelector('.product-upsell__image');
  const title = card?.querySelector('.product-upsell__title')?.textContent?.trim() ?? '';

  return {
    title,
    imageSrc: image instanceof HTMLImageElement ? getImageSrcForFly(image) : '',
  };
};

/**
 * Start the fly animation immediately (before cart/add completes).
 * Call succeed() on API success or fail() on error.
 * @param {Element | null | undefined} hostEl
 * @param {{ originEl?: Element, media?: { title: string, imageSrc: string } }} [options]
 * @returns {Promise<{ succeed: (onCartBump?: () => void) => void, fail: () => void } | null>}
 */
const startOptimisticFlyToCart = async (hostEl, options = {}) => {
  if (!(hostEl instanceof Element)) return null;

  const existing = optimisticSessions.get(hostEl);
  existing?.fail();

  const origin =
    options.originEl instanceof Element
      ? options.originEl
      : (() => {
          const override = flyOriginOverrides.get(hostEl);
          flyOriginOverrides.delete(hostEl);
          return override instanceof Element ? override : getAddToCartOrigin(hostEl);
        })();

  let imageSrc = options.media?.imageSrc ?? '';
  let imageIsReady = Boolean(imageSrc);

  if (!imageSrc) {
    const productRoot = hostEl.closest('.product') || hostEl.closest('.quick-view__container');
    const sourceImage = getVisibleGalleryImage(productRoot);

    if (!sourceImage) return null;

    imageSrc = getImageSrcForFly(sourceImage);
    imageIsReady = sourceImage.complete;
  } else {
    const cardImage = getUpsellCardFromHost(hostEl)?.querySelector('.product-upsell__image');
    imageIsReady = cardImage instanceof HTMLImageElement ? cardImage.complete : false;
  }

  if (!origin || !imageSrc) return null;

  let state = 'pending';
  let flyEl = null;
  let holdTimer = null;
  let animation = null;
  let cartTarget = null;
  let pendingOnCartBump = null;
  let startX = 0;
  let startY = 0;

  const cleanupFly = () => {
    window.clearTimeout(holdTimer);
    holdTimer = null;
    animation?.cancel();
    animation = null;
    flyEl?.remove();
    flyEl = null;
  };

  const removeSession = () => {
    optimisticSessions.delete(hostEl);
  };

  const completeSuccess = (target) => {
    bumpCartTarget(target, pendingOnCartBump);
    removeSession();
  };

  const fail = () => {
    if (state === 'done' || state === 'cancelled') return;

    state = 'cancelled';
    cleanupFly();
    removeSession();
  };

  const succeed = (onCartBump) => {
    if (state === 'cancelled') return;

    pendingOnCartBump = onCartBump;

    if (state === 'done') {
      completeSuccess(cartTarget);
    }
  };

  const session = { succeed, fail };
  optimisticSessions.set(hostEl, session);

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    state = 'done';
    cartTarget = getCartIconTarget();

    if (!cartTarget) {
      removeSession();
      return null;
    }

    return session;
  }

  if (!imageIsReady) {
    await preloadFlyImage(imageSrc);
  }

  if (state === 'cancelled') return null;

  const originRect = origin.getBoundingClientRect();
  ({ startX, startY } = getStartPositionFromOrigin(originRect));
  flyEl = createFlyElement(imageSrc, startX, startY);
  state = 'holding';

  holdTimer = window.setTimeout(() => {
    holdTimer = null;

    if (state === 'cancelled' || !flyEl || !document.body.contains(flyEl)) return;

    cartTarget = getCartIconTarget();

    if (!cartTarget) {
      state = 'done';
      flyEl.remove();
      flyEl = null;

      if (pendingOnCartBump) {
        pendingOnCartBump();
      }

      removeSession();
      return;
    }

    const { endX, endY } = getEndPositionForCartTarget(cartTarget);
    state = 'flying';

    animation = runFlyAnimation(flyEl, startX, startY, endX, endY, cartTarget, (target) => {
      animation = null;
      flyEl = null;
      state = 'done';

      if (pendingOnCartBump) {
        completeSuccess(target);
      } else {
        removeSession();
      }
    });
  }, FLY_HOLD_MS);

  return session;
};

/**
 * @param {Element | null | undefined} productFormEl
 * @returns {{ title: string, imageSrc: string }}
 */
const getProductFlyMedia = (productFormEl) => {
  const productRoot = productFormEl?.closest('.product') || productFormEl?.closest('.quick-view__container');
  const sourceImage = getVisibleGalleryImage(productRoot);
  const titleEl =
    document.querySelector('.product .product__title') || productRoot?.querySelector('.product__title, h1.product__title');

  return {
    title: titleEl?.textContent?.trim() ?? '',
    imageSrc: sourceImage ? getImageSrcForFly(sourceImage) : '',
  };
};

window.flyProductImageToCart = flyProductImageToCart;
window.startOptimisticFlyToCart = startOptimisticFlyToCart;
window.setFlyToCartOrigin = setFlyToCartOrigin;
window.getProductFlyMedia = getProductFlyMedia;
window.getUpsellFlyMedia = getUpsellFlyMedia;
